import { LocationWindow } from '../LocationWindow'
import { useState, type CSSProperties } from 'react'
import { Link } from 'react-router-dom'
import type { Arena as ArenaData, ArenaMode, ArenaOpponents, Rating, RatingBoard } from '@mezha/api'
import { useArenaQuery, useCancelRequestMutation, useCreateRequestMutation, useJoinRequestMutation, useRatingMeQuery, useRatingTopQuery } from '../../api/endpoints/arena'
import { useCommand } from '../../lib/useCommand'
import { clockText, countdown, durationText, useRefetchAt, useServerNow } from '../../lib/time'
import { usePhone } from '../../lib/useSize'
import { useShellMode } from '../../shell/ShellContext'
import { Emblem } from '../../ui/brand/Emblems'
import { Empty, Failure, Loading } from '../../ui/State'
import { ActionButton, cx } from '../../ui'
import './arena.css'

const TABS = [{ id: 'fights', label: 'Поединки' }, { id: 'honor', label: 'Доска почёта' }]
const BOARD_RU: Record<RatingBoard, string> = { people: 'с людьми', bots: 'с ботами' }

/** Next UTC midnight after the arena day: when the rated allowance comes back. */
const dayEnds = (dayUtc: string) => new Date(Date.parse(`${dayUtc}T00:00:00Z`) + 86_400_000)

/** `/play/arena`: modes, open requests of your bracket, your own request, rating. */
export function Arena() {
  useShellMode('window')
  const [tab, setTab] = useState('fights')
  const arena = useArenaQuery()
  return (
    <LocationWindow title="Арена" emblem="nav.arena" tabs={TABS} activeTab={tab} onTab={setTab} idBase="arena" bodyClassName="arena">
      {arena.isLoading && <Loading />}
      {arena.isError && !arena.data && <Failure error={arena.error} retry={() => void arena.refetch()} what="Арена" />}
      {arena.data && tab === 'fights' && <Fights arena={arena.data} refetch={() => void arena.refetch()} />}
      {tab === 'honor' && <Honor />}
    </LocationWindow>
  )
}

function Fights({ arena: a, refetch }: { arena: ArenaData; refetch: () => void }) {
  const now = useServerNow(1000)
  const phone = usePhone()
  const [modeId, setModeId] = useState<string>(a.own?.mode ?? a.modes[0]?.id ?? '')
  const [opponents, setOpponents] = useState<ArenaOpponents>('people')
  const bots = opponents === 'bots'
  const botXPLeft = Math.max(0, a.bot_rewards.limit - a.bot_rewards.rewarded_wins)
  const mode = a.modes.find(m => m.id === modeId) ?? a.modes[0]
  const [create] = useCreateRequestMutation()
  const [join] = useJoinRequestMutation()
  const [cancel] = useCancelRequestMutation()
  const cmd = useCommand(refetch, e => { if (e.code === 'request_closed') refetch() })
  const ratedLeft = a.daily.limit - a.daily.used
  const exhausted = (m: ArenaMode) => !bots && m.rated && ratedLeft <= 0
  const requests = a.requests.filter(r => r.mode === mode?.id && r.id !== a.own?.id && Date.parse(r.expires_at) > now)
  // Requests close themselves at expires_at: read the arena again when the nearest one does.
  const nextExpiry = [a.own?.expires_at, ...a.requests.map(r => r.expires_at)].filter((x): x is string => Boolean(x)).sort()[0]
  useRefetchAt(nextExpiry, refetch)

  // The request's action lives on the mode card (v04): search, fight bots, or cancel next to the queue.
  const search = mode && !a.own && (
    <div className="actions arena__go">
      <ActionButton gold locked={exhausted(mode)} onPress={() => cmd.run(() => create({ mode: mode.id, opponents }).unwrap())}>
        <Emblem name="arena.duel" size={16} />{exhausted(mode) ? 'Рейтинговые бои исчерпаны' : bots ? `Бой с ботами · ${mode.name}` : `Искать бой · ${mode.name}`}
      </ActionButton>
    </div>
  )

  return (
    <>
      <div className={phone ? 'mmodes' : 'modes'} role="radiogroup" aria-label="Виды поединков">
        {!phone && <div className="eyebrow">Виды поединков</div>}
        {a.modes.map(m => (
          <button key={m.id} type="button" role="radio" aria-checked={m.id === mode?.id} className={cx('mode', m.id === mode?.id && 'is-selected', exhausted(m) && 'is-locked')}
            onClick={() => setModeId(m.id)} disabled={a.own !== null && a.own.mode !== m.id}>
            <Emblem name={m.rated ? 'arena.rated' : m.per_side > 1 ? 'arena.team' : 'arena.duel'} />
            <span><b>{m.name}</b><small>{m.per_side > 1 ? `${m.per_side} на ${m.per_side}` : 'один на один'} · {m.rated ? 'рейтинг' : 'без рейтинга'}</small></span>
          </button>
        ))}
      </div>
      <div className="modecard">
        <div className="opps" role="radiogroup" aria-label="Соперники">
          {(['people', 'bots'] as const).map(o => (
            <button key={o} type="button" role="radio" aria-checked={opponents === o} className={cx('mode', 'opp', opponents === o && 'is-selected')}
              onClick={() => setOpponents(o)} disabled={a.own !== null && o === 'bots'}>
              <Emblem name={o === 'people' ? 'arena.people' : 'arena.bots'} />
              <span><b>{o === 'people' ? 'Только люди' : 'С ботами'}</b><small>{o === 'people' ? 'ждём соперников коридора' : 'бой начнётся сразу'}</small></span>
            </button>
          ))}
        </div>
        {mode && (
          <div className="parch">
            <h2>{mode.name}</h2>
            <div className="rules">
              <div>{mode.per_side > 1 ? `Команды по ${mode.per_side} бойца; цель выбирается каждый раунд` : 'Один на один, те же правила боя, что и на карте'}</div>
              {bots ? (
                <>
                  <div>{mode.per_side > 1 ? 'Свободные места займут боты' : 'Соперник — бот'} вашего уровня с обычной для него экипировкой</div>
                  {mode.rated ? <div>Рейтинг с ботами — отдельная доска; лимит боёв с людьми не тратится</div> : <div>Рейтинг не меняется</div>}
                  <div>{botXPLeft > 0
                    ? `Опыт за победу: +${a.bot_rewards.win_xp} · сегодня ещё ${botXPLeft} из ${a.bot_rewards.limit}`
                    : `Опыт за победы над ботами на сегодня получен — до ${clockText(dayEnds(a.daily.day_utc))}`}</div>
                </>
              ) : (
                <>
                  <div>Соперники из вашего коридора уровней</div>
                  {mode.rated
                    ? <div>Рейтинговых боёв сегодня: {a.daily.used} из {a.daily.limit}{ratedLeft <= 0 ? ` — исчерпаны до ${clockText(dayEnds(a.daily.day_utc))}` : ''}</div>
                    : <div>Рейтинг не меняется</div>}
                </>
              )}
              <div>Предметы не теряются</div>
            </div>
            {search}
          </div>
        )}
        {a.own && (
          <div className="queue slab" role="status">
            <div className="timer" style={{ '--p': `${Math.round((((now - Date.parse(a.own.created_at)) / 1000) % 60) / 60 * 100)}%` } as CSSProperties}>
              <b>{durationText(now - Date.parse(a.own.created_at))}</b>
            </div>
            <div>
              <b>Ищем соперника · {a.modes.find(m => m.id === a.own!.mode)?.name ?? a.own.mode}</b>
              <span>Сторона А: {a.own.side_a} из {a.own.per_side} · сторона Б: {a.own.side_b} из {a.own.per_side}</span>
              <span>{a.own.members.map(m => `${m.name}${m.you ? ' (вы)' : ''}, ур. ${m.level}`).join(' · ')}</span>
              <span>Заявка закроется сама через {countdown(now, a.own.expires_at).text}, если соперники не найдутся</span>
              {a.few_opponents && <small>Мало бойцов в вашем коридоре — ожидание может затянуться. Пока можно пойти <Link to="/play/map">на карту за встречами</Link>.</small>}
            </div>
            <ActionButton className="arena__cancel" onPress={() => cmd.run(() => cancel({ id: a.own!.id }).unwrap())}>Отменить поиск</ActionButton>
          </div>
        )}
        {!a.own && !bots && a.few_opponents && <p className="note">Сейчас мало бойцов вашего уровня. Заявка подождёт соперника; неравный бой не создаётся — или выберите бой с ботами.</p>}
        {!bots && <>
        <div className="eyebrow">Открытые заявки</div>
        {requests.length === 0 ? <p className="note">Заявок этого вида нет — создайте свою.</p> : (
          <ul className="lboard">
            {requests.map(r => (
              <li key={r.id} className="brow arena__req">
                <i>ур. {r.level}</i>
                <span>{r.side_a}+{r.side_b} из {r.per_side * 2} · ждёт {durationText(now - Date.parse(r.created_at))}</span>
                <ActionButton locked={Boolean(a.own)} onPress={() => cmd.run(() => join({ id: r.id }).unwrap())}>Вступить</ActionButton>
              </li>
            ))}
          </ul>
        )}
        </>}
        {cmd.text && <p className="arena__error" role="alert">{cmd.text}</p>}
      </div>
      {!phone && <Standing board={bots ? 'bots' : 'people'} />}
    </>
  )
}

function Standing({ board }: { board: RatingBoard }) {
  const me = useRatingMeQuery(board)
  return (
    <div className="standing">
      <div className="eyebrow">Ваше место · {BOARD_RU[board]}</div>
      {me.isLoading && <Loading />}
      {me.isError && !me.data && <Failure error={me.error} retry={() => void me.refetch()} what="Рейтинг" />}
      {me.data && <MyRating r={me.data} />}
      <Top board={board} me={me.data} limit={8} />
    </div>
  )
}

function MyRating({ r }: { r: Rating }) {
  return (
    <>
      <div className="rating slab"><Emblem name="arena.rating" /><div><b>{r.rating}</b><span>рейтинг</span></div></div>
      <div className="wl">
        <div className="slab"><b>{r.wins}</b>побед</div>
        <div className="slab"><b>{r.losses}</b>поражений</div>
        <div className="slab"><b>{r.draws}</b>ничьих</div>
      </div>
    </>
  )
}

function Top({ board, me, limit }: { board: RatingBoard; me: Rating | undefined; limit?: number }) {
  const top = useRatingTopQuery(board)
  const items = top.data?.items ?? []
  const shown = limit ? items.slice(0, limit) : items
  return (
    <>
      <div className="eyebrow">Доска почёта · {BOARD_RU[board]}</div>
      {top.isLoading && <Loading />}
      {top.isError && !top.data && <Failure error={top.error} retry={() => void top.refetch()} what="Доска почёта" />}
      {top.data && items.length === 0 && <p className="note">Рейтинговых боёв ещё не было.</p>}
      <ol className="lboard">
        {shown.map((r, i) => (
          <li key={`${r.name}-${i}`} className={cx('brow', i < 3 && 'is-top', me && r.name === me.name && 'is-me')}><i>{i + 1}</i>{r.name}<b>{r.rating}</b></li>
        ))}
      </ol>
    </>
  )
}

function Honor() {
  const [board, setBoard] = useState<RatingBoard>('people')
  const me = useRatingMeQuery(board)
  return (
    <div className="arena__honor">
      <div className="standing">
        <div className="opps" role="radiogroup" aria-label="Доска">
          {(['people', 'bots'] as const).map(b => (
            <button key={b} type="button" role="radio" aria-checked={board === b} className={cx('mode', 'opp', board === b && 'is-selected')} onClick={() => setBoard(b)}>
              <Emblem name={b === 'people' ? 'arena.people' : 'arena.bots'} /><span><b>{b === 'people' ? 'С людьми' : 'С ботами'}</b></span>
            </button>
          ))}
        </div>
        {me.data ? <MyRating r={me.data} /> : me.isError ? <Failure error={me.error} retry={() => void me.refetch()} what="Рейтинг" /> : <Loading />}
      </div>
      <div className="modecard">
        <Top board={board} me={me.data} />
        {me.data && !me.data.wins && !me.data.losses && !me.data.draws && <Empty>Вы ещё не бились в рейтинговых боях.</Empty>}
      </div>
    </div>
  )
}
